import { DRAKE_HOVER, SWEEP_REST } from '../../models/glass-drake.ts';
import { clip, type Bones, type Clip } from '../clip.ts';
import { TAU, cwave, envelope, inCubic, inOut, inQuad, keys, lerp, outBack, outCubic, outExpo, outQuad, phase, settle, smooth, span, wave } from '../curves.ts';
import { apply, timeline, type Ch, type KeyPose } from '../pose.ts';

/*
 * Draco de vidrio (models/glass-drake.ts): the era V flyer, a grown child of the Burn that hangs over a sector
 * and sweeps it with a cone of fire. Built on the wyrmling's bones and conventions (anim/creatures/wyrmling.ts),
 * but grown: every beat is slower and deeper, the body heaving on it like a ship on a swell, the heavy head held
 * steady on the long neck. It hovers at its rest height and every clip keeps it in the air except `die`.
 *
 * Wings are authored for the right one (+X) and mirrored: on `arm` +Z lifts it, +Y sweeps it back, +X turns the
 * leading edge down; on `fore` −Y folds the forearm in, on `hand` +Y folds the fingers back. +X pitches the body,
 * the neck and the head nose down, opens the jaw, lifts the tail and swings the legs back. `throat` is scaled
 * (it swells before the fire); `breath` is the cone of fire, built tiny: scaled by `CONE` it shows at full size.
 *
 * Blows: attack (a rake of the jaws and forelegs from the air) lands at 0.2 s. Sweep: it draws in (0–0.6 s), the
 * cone pours out from 0.65 s to 1.85 s, swinging from its left across to its right and back (the far left at
 * 0.65 s, the centre at 0.95 s, the far right at 1.25 s, back to the centre at 1.85 s), and it shakes off the heat.
 */

type Side = 'L' | 'R';
type B =
  | 'root' | 'body' | 'neck1' | 'neck2' | 'neck3' | 'head' | 'jaw' | 'throat' | 'breath'
  | `arm${Side}` | `fore${Side}` | `hand${Side}`
  | 'tail1' | 'tail2' | 'tail3' | 'tail4' | 'tail5'
  | `leg${'F' | 'B'}${Side}` | `shin${'F' | 'B'}${Side}`;
type P = KeyPose<B>;

const sin = Math.sin;
const TAIL = ['tail1', 'tail2', 'tail3', 'tail4', 'tail5'] as const;
/** How far the cone of fire is scaled up to show at full size. */
const CONE = 1 / SWEEP_REST;
/** The ground under the hovering origin, less the body on its side. */
const GROUND = -DRAKE_HOVER + 0.36;

const mirror = (c: Ch): Ch => [c[0], -c[1], -c[2], -(c[3] ?? 0), c[4] ?? 0, c[5] ?? 0, c[6] ?? 1];
/** Both wings from the right one's arm, forearm and hand. */
const wings = (arm: Ch, fore: Ch = [0, 0, 0], hand: Ch = [0, 0, 0]): P => ({
  armR: arm, foreR: fore, handR: hand, armL: mirror(arm), foreL: mirror(fore), handL: mirror(hand),
});
/** All four legs: [thigh, shin] front and back, the same on both sides. */
const legs = (front: [Ch, Ch], back: [Ch, Ch] = [[0, 0, 0], [0, 0, 0]]): P => ({
  legFR: front[0], shinFR: front[1], legFL: mirror(front[0]), shinFL: mirror(front[1]),
  legBR: back[0], shinBR: back[1], legBL: mirror(back[0]), shinBL: mirror(back[1]),
});
/** A neck curl shared down the chain (heavier at the base); the head on its own; `y` turns it. */
const neck = (x: number, head: number, y = 0): P => ({
  neck1: [x * 0.42, y * 0.4, 0], neck2: [x * 0.33, y * 0.33, 0], neck3: [x * 0.25, y * 0.27, 0], head: [head, y * 0.2, 0],
});
/** A tail curl growing toward the tip: +x lifts it, +y swings it to the left (−X). */
const tail = (x: number, y = 0): P => {
  const p: P = {};
  TAIL.forEach((n, i) => { p[n] = [x * (0.6 + i * 0.2), y * (0.6 + i * 0.2), 0]; });
  return p;
};
const S = (k: number): Ch => [0, 0, 0, 0, 0, 0, k];
const sum = (...ps: P[]): P => {
  const out: Partial<Record<B, number[]>> = {};
  for (const p of ps) {
    for (const [k, c] of Object.entries(p) as [B, Ch][]) {
      const cur = out[k] ?? [0, 0, 0, 0, 0, 0, 1];
      for (let i = 0; i < 7; i++) {
        const v = c[i] ?? (i === 6 ? 1 : 0);
        cur[i] = i === 6 ? (cur[i] as number) * v : (cur[i] as number) + v;
      }
      out[k] = cur;
    }
  }
  return out as unknown as P;
};

/** A wing's pose for the right side: arm, forearm and hand rotations. */
interface W { ax: number; ay: number; az: number; fy: number; fz: number; hx: number; hy: number; hz: number }

/** One wingbeat at `p` (0 = top of the stroke, wings high; loops at 1). */
const beatAt = (p: number): W => ({
  az: keys(p, [[0, 0.82], [0.4, -0.62, inOut], [1, 0.82, inOut]]),
  ay: keys(p, [[0, 0.04], [0.2, -0.12], [0.42, 0.04], [0.7, 0.28], [1, 0.04]]),
  ax: keys(p, [[0, 0], [0.2, 0.15], [0.42, 0.05], [0.7, -0.18], [1, 0]]),
  fz: keys(p, [[0, 0.12], [0.2, 0.26], [0.46, -0.18], [0.7, -0.32], [0.92, 0.05], [1, 0.12]]),
  fy: keys(p, [[0, 0], [0.4, 0.02], [0.6, -0.42], [0.8, -0.46], [1, 0]]),
  hz: keys(p, [[0, 0.16], [0.24, 0.32], [0.48, -0.24], [0.74, -0.36], [0.93, 0.12], [1, 0.16]]),
  hy: keys(p, [[0, 0], [0.4, 0.02], [0.6, 0.55], [0.8, 0.62], [1, 0]]),
  hx: keys(p, [[0, 0], [0.24, 0.08], [0.7, -0.1], [1, 0]]),
});
/** Held out to glide: a shallow V, the fingers a touch swept. */
const GLIDE: W = { az: 0.08, ay: 0.08, ax: 0.04, fy: -0.04, fz: 0.04, hx: 0, hy: 0.1, hz: 0.06 };
const mix = (a: W, b: W, k: number): W => ({
  ax: lerp(a.ax, b.ax, k), ay: lerp(a.ay, b.ay, k), az: lerp(a.az, b.az, k), fy: lerp(a.fy, b.fy, k), fz: lerp(a.fz, b.fz, k),
  hx: lerp(a.hx, b.hx, k), hy: lerp(a.hy, b.hy, k), hz: lerp(a.hz, b.hz, k),
});
/** Scales a beat's swing about the glide pose (`amp` < 1 beats shallower). */
const scaled = (w: W, amp: number): W => mix(GLIDE, w, amp);
const wingR = (b: Bones<B>, w: W): void => {
  b.armR.rot(w.ax, w.ay, w.az);
  b.foreR.rot(0, w.fy, w.fz);
  b.handR.rot(w.hx, w.hy, w.hz);
};
const wingL = (b: Bones<B>, w: W): void => {
  b.armL.rot(w.ax, -w.ay, -w.az);
  b.foreL.rot(0, -w.fy, -w.fz);
  b.handL.rot(w.hx, -w.hy, -w.hz);
};
const both = (b: Bones<B>, w: W): void => {
  wingR(b, w);
  wingL(b, w);
};
/** A wing pose as key-pose channels (both sides). */
const wingPose = (w: W): P => wings([w.ax, w.ay, w.az], [0, w.fy, w.fz], [w.hx, w.hy, w.hz]);
const TOP = wingPose(beatAt(0));

/** What a beat does to the body: it heaves up on the downstroke and sags on the recovery; tail and legs trail. */
const lift = (b: Bones<B>, p: number, amp = 1): void => {
  b.root.pos(0, 0.08 * amp * sin(TAU * (p - 0.2)), 0);
  b.body.rot(-0.05 * amp * sin(TAU * (p - 0.05)));
  // The neck gimbals: the head stays steady while the trunk heaves.
  b.neck1.rot(0.03 * amp * sin(TAU * (p - 0.05)));
  b.neck3.rot(0.015 * amp * sin(TAU * (p - 0.05)));
  b.head.rot(0.025 * amp * sin(TAU * (p - 0.05)));
  TAIL.forEach((n, i) => b[n].rot(0.05 * amp * (1 + i * 0.2) * sin(TAU * (p - 0.12 - i * 0.07))));
  for (const s of ['L', 'R'] as const) {
    b[`legF${s}`].rot(0.06 * amp * sin(TAU * (p - 0.25)));
    b[`shinF${s}`].rot(-0.08 * amp * sin(TAU * (p - 0.35)));
    b[`legB${s}`].rot(0.08 * amp * sin(TAU * (p - 0.3)));
    b[`shinB${s}`].rot(0.1 * amp * sin(TAU * (p - 0.4)));
  }
};

/** The hovering stance: trunk reared on the air, the long neck curving up and the head looking down, legs hanging. */
const STANCE: P = sum(
  { body: [-0.3, 0, 0], neck1: [0.02, 0, 0], neck2: [0.1, 0, 0], neck3: [0.16, 0, 0], head: [0.38, 0, 0], jaw: [0.05, 0, 0] },
  { tail1: [-0.12, 0, 0], tail2: [0.06, 0, 0], tail3: [0.1, 0, 0], tail4: [0.1, 0, 0], tail5: [0.08, 0, 0] },
  legs([[0.1, 0, 0], [-0.35, 0, 0]], [[0.3, 0, 0], [0.25, 0, 0]]),
);
const HOVER: P = sum(STANCE, TOP);

/** A smooth bump over a loop phase centred at `c` (cycles), `w` wide: loop-safe accents (a look, a snap). */
const bump = (p: number, c: number, w: number): number => {
  let d = Math.abs(p - c);
  d = Math.min(d, 1 - d);
  return d >= w ? 0 : smooth(1 - d / w);
};
/** A tremble: fast and small, for strain. */
const shiver = (t: number, f = 13, off = 0): number => sin(TAU * (t * f + off)) * 0.6 + sin(TAU * (t * f * 1.7 + off * 2)) * 0.4;

/** Hovering in place: two strong beats, the body heaving, the big head steady and turning to look, the tail swaying. */
const idle = (): Clip<B> =>
  clip<B>('idle', 2.0, true, (b, t) => {
    const P = phase(t, 2.0);
    const p = phase(t, 1.0);
    const amp = 1 + 0.06 * wave(P, 0.1);
    apply(b, STANCE);
    both(b, scaled(beatAt(p), amp));
    lift(b, p, amp * 1.15);
    b.body.rot(0.03 * wave(P, 0.2));
    b.neck1.rot(0, 0.1 * wave(P, 0.3));
    b.neck2.rot(0, 0.06 * wave(P, 0.35));
    b.head.rot(0.06 * bump(P, 0.6, 0.15), 0.32 * bump(P, 0.25, 0.2) - 0.28 * bump(P, 0.72, 0.18), 0.12 * bump(P, 0.25, 0.2));
    b.jaw.rot(0.2 * bump(P, 0.82, 0.07));
    // The fire in the throat breathes, swelling a little with each beat.
    b.throat.scale(1 + 0.06 * wave(p, 0.1) + 0.04 * wave(P, 0.4));
    TAIL.forEach((n, i) => b[n].rot(0, 0.09 * wave(P, -i * 0.1) * (1 + i * 0.2), 0));
    b.root.pos(0.03 * wave(P, 0.1), 0.03 * wave(P, 0.35), 0.02 * cwave(P, 0.2)).rot(0, 0.05 * wave(P, 0.05), 0.04 * wave(P, 0.25));
  });

/** Flying on: the body laid along the air and the neck stretched out ahead, two driving beats and a short glide. */
const walk = (dur = 2.3): Clip<B> =>
  clip<B>('walk', dur, true, (b, t) => {
    const P = phase(t, dur);
    const beat = 0.8;
    if (t < beat * 2) {
      const p = phase(t, beat);
      both(b, beatAt(p));
      lift(b, p, 1.15);
    } else {
      const u = (t - beat * 2) / (dur - beat * 2);
      const k = envelope(u, 0, 0.35, 0.65, 1, inOut);
      both(b, mix(beatAt(0), GLIDE, k));
      lift(b, 0, 1.15 * (1 - k));
    }
    b.root.pos(0, 0.05 * cwave(P, 0.3), 0).rot(0, 0.04 * wave(P, 0.1), 0.08 * wave(P, 0.55));
    b.body.rot(0.04 + 0.03 * wave(P, 0.2));
    // The neck reaches out level, the head pitched to keep its eye on the ground ahead.
    b.neck1.rot(-0.12, 0.05 * wave(P, 0.7));
    b.neck2.rot(-0.04, 0.04 * wave(P, 0.75));
    b.neck3.rot(0.04, 0.03 * wave(P, 0.8));
    b.head.rot(0.22, -0.06 * wave(P, 0.7), -0.05 * wave(P, 0.55));
    // The whip streams behind in a travelling S.
    TAIL.forEach((n, i) => b[n].rot(0.03 + 0.04 * wave(P, -i * 0.15, 2), 0.1 * wave(P, -i * 0.13) * (1 + i * 0.25), 0.04 * wave(P, -i * 0.1)));
    // Legs drawn up along the belly, forelegs folded back, hind legs trailing.
    for (const s of ['L', 'R'] as const) {
      b[`legF${s}`].rot(0.9);
      b[`shinF${s}`].rot(-0.9);
      b[`legB${s}`].rot(0.95);
      b[`shinB${s}`].rot(0.5);
    }
    b.throat.scale(1 + 0.04 * wave(P, 0.2, 2));
  });

/** From the air: cocked high, neck drawn back, jaws open and forelegs up; at 0.13 s it snaps and rakes down. */
const attack = (): Clip<B> => {
  const cocked = sum(STANCE, neck(-0.55, -0.3), TOP, tail(-0.15), legs([[-0.9, 0, 0], [-0.6, 0, 0]], [[0.1, 0, 0], [0.2, 0, 0]]), {
    root: [0, 0, 0, 0, 0.06, -0.06], body: [-0.2, 0, 0], jaw: [0.75, 0, 0], throat: S(1.25),
  });
  const drawn = sum(cocked, neck(-0.12, -0.08), { jaw: [0.1, 0, 0], body: [-0.04, 0, 0] });
  const snap = sum(STANCE, neck(0.65, 0.3), wingPose(beatAt(0.4)), tail(-0.1), legs([[0.75, 0, 0], [0.2, 0, 0]], [[0.4, 0, 0], [0.3, 0, 0]]), {
    root: [0, 0, 0, 0, -0.04, 0.16], body: [0.32, 0, 0], jaw: [-0.05, 0, 0], throat: S(0.9),
  });
  const after = sum(snap, neck(-0.1, 0.05), wingPose(beatAt(0.55)), wings([0, 0, 0.62]), legs([[0.15, 0, 0], [0.3, 0, 0]]), {
    root: [0, 0, 0, 0, 0.02, -0.06], jaw: [0.15, 0, 0],
  });
  return clip<B>('attack', 1.15, false, (b, t) => {
    timeline(b, t, [
      [0, cocked],
      [0.1, drawn, outQuad],
      [0.2, snap, inCubic],
      [0.36, after, outQuad],
      [0.56, sum(STANCE, neck(0.2, 0.1), wingPose(beatAt(0.75)), { jaw: [0.2, 0, 0] }), inOut],
      [0.84, sum(STANCE, wingPose(beatAt(0.92)), { root: [0, 0, 0, 0, 0.02, 0] }), inOut],
      [1.15, HOVER, inOut],
    ], { neck3: 0.008, head: 0.014, jaw: 0.004, tail2: 0.02, tail3: 0.04, tail4: 0.06, tail5: 0.08, handL: 0.02, handR: 0.02, shinFL: 0.015, shinFR: 0.015 });
    // A shudder through the neck as the jaws close on something.
    const k = envelope(t, 0.19, 0.21, 0.28, 0.4);
    b.head.rot(0.04 * shiver(t) * k);
  }, (t) => {
    const k = 1 - span(t, 0.12, 0.22);
    const hitK = Math.max(0, settle(t - 0.2, 4, 12)) * span(t, 0.19, 0.21);
    return [1 + 0.03 * k - 0.02 * hitK, 1 + 0.02 * k - 0.03 * hitK, 1 + 0.04 * hitK];
  });
};

/** Struck in the air: thrown back and rolled, the wings flung up out of time, the neck whipping; it rights itself. */
const hit = (): Clip<B> =>
  clip<B>('hit', 0.5, false, (b, t) => {
    timeline(b, t, [
      [0, HOVER],
      [0.06, sum(STANCE, neck(-0.55, -0.35, 0.3), tail(-0.3, 0.3), legs([[-0.4, 0, 0], [-0.3, 0, 0]], [[-0.3, 0, 0], [0, 0, 0]]), {
        root: [0, 0.15, 0.35, 0, 0.08, -0.16], body: [-0.35, 0, 0], jaw: [0.6, 0, 0], throat: S(1.25),
        armR: [-0.2, -0.25, 1.05], foreR: [0, 0.2, 0.4], handR: [0, -0.1, 0.5], armL: [0.2, -0.35, -0.35], foreL: [0, 0.5, 0.2], handL: [0, -0.6, 0.3],
      }), outExpo],
      [0.18, sum(STANCE, neck(0.2, 0.15, -0.1), tail(0.12, -0.1), wings([0, 0.05, -0.5], [0, -0.1, -0.2], [0, 0.15, -0.25]), { root: [0, -0.05, -0.12, 0, -0.04, -0.08], body: [0.1, 0, 0], jaw: [0.25, 0, 0], throat: S(0.9) }), inOut],
      [0.33, sum(STANCE, wingPose(beatAt(0.6)), { root: [0, 0, 0.04, 0, 0.02, -0.03] }), inOut],
      [0.5, HOVER, inOut],
    ], { neck3: 0.01, head: 0.02, jaw: 0.01, tail3: 0.03, tail4: 0.05, tail5: 0.07, handL: 0.025, handR: 0.02 });
  }, (t) => {
    const k = span(t, 0, 0.05, outQuad) * (1 - span(t, 0.08, 0.36, inOut));
    return [1 - 0.05 * k, 1 + 0.06 * k, 1 - 0.04 * k];
  });

/** Dazed: it lurches through the air on ragged, out-of-step beats, sagging and rolling, the heavy head lolling. */
const stun = (): Clip<B> =>
  clip<B>('stun', 1.8, true, (b, t) => {
    const P = phase(t, 1.8);
    apply(b, STANCE);
    // The right wing beats three times, the left twice: they never fall in step.
    wingR(b, scaled(beatAt(phase(t, 0.6)), 0.75 + 0.2 * wave(P, 0.1)));
    wingL(b, scaled(beatAt(phase(t + 0.25, 0.9)), 0.55 + 0.25 * wave(P, 0.6)));
    b.root.pos(0.06 * wave(P, 0.3), -0.25 + 0.08 * wave(P, 0, 2), 0.05 * cwave(P)).rot(0.15 * wave(P, 0.25), 0.3 * wave(P, 0.05), 0.4 * wave(P) + 0.12 * wave(P, 0.2, 3));
    b.body.rot(0.25 + 0.12 * wave(P, 0.4, 2), 0, 0.1 * wave(P, 0.7));
    b.neck1.rot(0.22 + 0.1 * wave(P, 0.1, 2), 0.22 * wave(P, 0.15));
    b.neck2.rot(0.16 + 0.1 * wave(P, 0.2, 2), 0.18 * wave(P, 0.25));
    b.neck3.rot(0.12 + 0.08 * wave(P, 0.3, 2), 0.14 * wave(P, 0.3));
    b.head.rot(0.2 + 0.2 * wave(P, 0.15, 2), 0.3 * cwave(P, 0.15), 0.45 * wave(P, 0.15));
    b.jaw.rot(0.35 + 0.12 * wave(P, 0.3, 3));
    b.throat.scale(0.8 + 0.08 * wave(P, 0.2, 3));
    TAIL.forEach((n, i) => b[n].rot(-0.1 + 0.08 * wave(P, 0.3 - i * 0.1, 2), 0.18 * wave(P, 0.3 - i * 0.12), 0.1 * wave(P, -i * 0.1)));
    for (const s of ['L', 'R'] as const) {
      const o = s === 'L' ? 0 : 0.25;
      b[`legF${s}`].rot(-0.2 + 0.3 * wave(P, 0.35 + o, 2));
      b[`shinF${s}`].rot(0.2 * wave(P, 0.45 + o, 2));
      b[`legB${s}`].rot(-0.1 + 0.3 * wave(P, 0.6 + o, 2));
      b[`shinB${s}`].rot(0.3 + 0.2 * wave(P, 0.7 + o, 2));
    }
  });

/** Brought down: it screams at the sky, the wings give and fold, it falls turning and smashes down on its side. */
const die = (): Clip<B> =>
  clip<B>('die', 2.1, false, (b, t) => {
    const heap: P = sum(tail(0.1, 0.12), legs([[-0.5, 0, 0.2], [0.8, 0, 0]], [[-0.4, 0, -0.2], [1.0, 0, 0]]), {
      root: [0.1, 0.35, 1.25, 0, GROUND, 0.25], body: [0.08, 0, 0], neck1: [0.05, 0.3, 0.15], neck2: [0.0, 0.3, 0.1], neck3: [-0.05, 0.2, 0.1],
      head: [-0.1, 0.15, 0.35], jaw: [0.5, 0, 0], throat: S(0.4),
      armR: [0.25, 0.45, -1.1], foreR: [0, -0.95, -0.35], handR: [0.1, 1.2, -0.45],
      armL: [0.1, -0.35, -1.3], foreL: [0, 0.35, 0.05], handL: [0, -0.55, 0.25],
    });
    const bounce: P = sum(heap, { root: [0, 0, -0.1, 0, 0.07, 0], armR: [0, 0, 0.3], handR: [0, -0.3, 0.2], neck1: [-0.15, 0, 0], tail2: [0.12, 0, 0] });
    timeline(b, t, [
      [0, HOVER],
      [0.18, sum(STANCE, neck(-0.8, -0.55), tail(-0.25), wings([0, 0.15, 1.05], [0, 0.1, 0.3], [0, -0.1, 0.35]), legs([[-0.5, 0, 0], [-0.4, 0, 0]], [[-0.4, 0, 0], [0, 0, 0]]), {
        root: [0, 0, 0, 0, 0.12, -0.05], body: [-0.3, 0, 0], jaw: [1.0, 0, 0], throat: S(1.5),
      }), outCubic],
      [0.53, sum(neck(-0.15, -0.1), tail(0.1, 0.15), legs([[0.4, 0, 0], [0, 0, 0]], [[0.3, 0, 0], [0.3, 0, 0]]), {
        root: [0.15, 0.1, 0.45, 0, -0.2, 0.08], body: [0.2, 0, 0], jaw: [0.6, 0, 0], throat: S(0.8),
        armR: [0, 0.5, 0.75], foreR: [0, -0.8, 0.1], handR: [0, 1.0, -0.2], armL: [0, -0.6, -0.4], foreL: [0, 0.7, 0.1], handL: [0, -0.9, 0.1],
      }), inOut],
      [1.0, sum(heap, tail(-0.25, -0.1), { root: [0.12, 0.25, 0.95, 0, GROUND + 0.1, 0.22], armR: [0, 0, 0.5], armL: [0, 0, -1.2], neck1: [0, 0.3, 0], neck2: [0, 0.2, 0], throat: S(1.6) }), inQuad],
      [1.16, bounce, outQuad],
      [1.38, heap, inQuad],
      [2.1, heap, outCubic],
    ], { neck2: 0.015, neck3: 0.03, head: 0.045, jaw: 0.02, tail2: 0.02, tail3: 0.04, tail4: 0.06, tail5: 0.08, handL: 0.04, handR: 0.05, armR: 0.02 });
    // A last spasm of the scream; the fire flares in the throat on impact, then gutters out.
    const k = envelope(t, 0.12, 0.2, 0.38, 0.5);
    b.head.rot(0.05 * shiver(t, 12) * k);
    b.jaw.rot(0.08 * shiver(t, 17) * k);
    const tw = envelope(t, 1.44, 1.56, 1.62, 1.8);
    b.tail5.rot(0, 0.25 * tw * sin(TAU * t * 6));
    b.handR.rot(0, 0, 0.15 * tw);
  }, (t) => {
    // The crash flattens it for a beat.
    const k = Math.max(0, settle(t - 1.0, 3, 9)) * span(t, 0.99, 1.01);
    return [1 + 0.08 * k, 1 - 0.12 * k, 1 + 0.06 * k];
  });

/**
 * Barrido: the fire in the throat swells until it glows through the glass and it rears on the air (0–0.6 s);
 * then it drops its head and the cone of fire pours out at the ground ahead (0.65 s), and the neck carries it from
 * its left across to its right and back again while the wings beat short and hard to hold it up (to 1.85 s); it
 * lets go and shakes off the heat.
 */
const sweep = (): Clip<B> => {
  const draw = sum(STANCE, neck(-0.65, -0.4), tail(-0.15), wings([0, 0, 0.35]), {
    root: [0, 0, 0, 0, 0.14, -0.1], body: [-0.2, 0, 0], jaw: [0.25, 0, 0], throat: S(2.1),
  });
  const pour = sum(STANCE, neck(0.12, -0.08), tail(-0.1), legs([[0.5, 0, 0], [-0.2, 0, 0]], [[0.4, 0, 0], [0.2, 0, 0]]), {
    root: [0, 0, 0, 0, 0.0, -0.1], body: [0.12, 0, 0], jaw: [1.0, 0, 0], throat: S(1.3), breath: S(CONE),
  });
  const spent = sum(pour, neck(-0.05, -0.05), { jaw: [-0.1, 0, 0], throat: S(0.6), breath: S(0.85) });
  return clip<B>('sweep', 2.4, false, (b, t) => {
    timeline(b, t, [
      [0, STANCE],
      [0.5, draw, inOut],
      [0.65, pour, outBack],
      [1.85, spent, (k) => k],
      [1.98, sum(STANCE, neck(0.2, 0.1), { jaw: [0.3, 0, 0], throat: S(0.75) }), outQuad],
      [2.4, STANCE, inOut],
    ], { neck3: 0.01, head: 0.018, jaw: 0.008, tail3: 0.04, tail4: 0.06, tail5: 0.08 });
    const hold = envelope(t, 0.6, 0.7, 1.85, 2.0);
    const p = phase(t, 0.6);
    both(b, scaled(beatAt(p), 1 - 0.45 * hold));
    lift(b, p, 1 - 0.5 * hold);
    // Left (+Y) to right and back: −cos over one pass, from the far left at 0.65 s.
    const sw = Math.cos(TAU * span(t, 0.65, 1.85, (k) => k)) * hold;
    b.neck1.rot(0, sw * 0.42);
    b.neck2.rot(0, sw * 0.34);
    b.neck3.rot(0, sw * 0.22, -sw * 0.1);
    b.root.rot(0, sw * 0.16, -sw * 0.08);
    b.head.rot(0.03 * shiver(t) * hold, -sw * 0.1, 0.02 * shiver(t, 9) * hold);
    b.jaw.rot(0.05 * shiver(t, 16) * hold);
    b.breath.scale(1 + 0.1 * shiver(t, 10, 0.2) * hold);
    b.throat.scale(1 + 0.08 * shiver(t, 7) * hold + 0.06 * shiver(t, 23) * envelope(t, 0.2, 0.45, 0.5, 0.65));
  }, (t) => {
    const k = envelope(t, 0.1, 0.45, 0.5, 0.7);
    return [1 + 0.035 * k, 1 + 0.025 * k, 1];
  }, 'Barrido de fuego');
};

/**
 * Hover: holding its place high over a sector (before a sweep), on great slow beats, the head down and turning
 * across the ground below as if choosing; the tail hangs and sways.
 */
const hover = (): Clip<B> =>
  clip<B>('hover', 2.6, true, (b, t) => {
    const P = phase(t, 2.6);
    const p = phase(t, 1.3);
    apply(b, STANCE);
    both(b, scaled(beatAt(p), 1.12));
    lift(b, p, 1.35);
    b.root.pos(0, 0.12 + 0.04 * wave(P, 0.3), 0).rot(0.04, 0.06 * wave(P), 0.03 * wave(P, 0.25));
    b.body.rot(-0.08 + 0.03 * wave(P, 0.2));
    b.neck1.rot(0.08, 0.16 * wave(P, 0.1));
    b.neck2.rot(0.06, 0.1 * wave(P, 0.15));
    b.neck3.rot(0.04, 0.05 * wave(P, 0.2));
    b.head.rot(0.18, 0.22 * wave(P, 0.25), 0.06 * wave(P, 0.25));
    b.jaw.rot(0.12 + 0.08 * wave(P, 0.4, 2));
    b.throat.scale(1.08 + 0.08 * wave(p, 0.1));
    TAIL.forEach((n, i) => b[n].rot(-0.08 + 0.03 * wave(P, -i * 0.1, 2), 0.12 * wave(P, -i * 0.1) * (1 + i * 0.2), 0));
  }, undefined, 'Sostenerse en el aire');

/** Its clips (see models/glass-drake.ts for the bones). */
export const glassDrakeClips = (): Clip[] => [idle(), walk(), attack(), hit(), stun(), die(), sweep(), hover()] as Clip[];

/** Clips by library kind (see ../rigs.ts). */
export const CLIPS: Record<string, () => Clip[]> = { glassDrake: glassDrakeClips };
